import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, noop } from 'lodash-es';
import Label from 'reactstrap/lib/Label';
import Input from 'reactstrap/lib/Input';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { getMetricsPageHeaders } from 'client/utils/metrics-header';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { getVinDecoderStylesData } from 'client/data/models/vehicle-vin';
import { normalizeVin, transformVinDecoder } from 'site-modules/shared/utils/price-checker';

import { ENTER_KEY_VALUE } from 'site-modules/shared/constants/key-values';
import { PRICE_CHECKER_CREATIVE_ID } from 'site-modules/shared/constants/price-checker';

const TRACKING_PARAMS = {
  action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
  action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
  creative_id: PRICE_CHECKER_CREATIVE_ID,
};

export function VinInputUI({
  className = null,
  defaultVin = '',
  updateVinParams,
  onInputChange,
  errorMessageText = 'We don’t recognize this as a valid VIN number. Please confirm VIN number with the seller of this vehicle.',
  apiMetrics,
}) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [vinValue, setVinValue] = useState(() => normalizeVin(defaultVin));
  const inventoryPromiseCancelable = useRef(null);
  const stylesPromiseCancelable = useRef(null);

  const updateVinData = useCallback(
    async (value, skipInputChange) => {
      const normalizedVin = normalizeVin(value);

      return Promise.resolve(validation.validateVIN(normalizedVin))
        .then(isValidVin => {
          if (!isValidVin) {
            return Promise.reject();
          }

          return getVinDecoderStylesData(normalizedVin, null, {
            headers: getMetricsPageHeaders({
              actionName: 'vin.vinDecoderBasicInfo',
              ...apiMetrics,
            }),
          });
        })
        .then(({ styles, modelYear }) => {
          const vinStylesData = transformVinDecoder({ styles, modelYear });
          if (!vinStylesData?.styleId) {
            return Promise.reject();
          }

          setErrorMessage(null);

          return vinStylesData;
        })
        .catch(() => {
          TrackingHandler.fireEvent('pc_cant_decode_vin', {
            event_data: { ...TRACKING_PARAMS, value: normalizedVin },
          });

          setErrorMessage(errorMessageText);
        })
        .then(newVinParams => {
          updateVinParams(
            {
              vin: newVinParams ? normalizedVin : null,
              makeName: get(newVinParams, 'makeName'),
              makeSlug: get(newVinParams, 'makeSlug'),
              modelName: get(newVinParams, 'modelName'),
              modelSlug: get(newVinParams, 'modelSlug'),
              year: get(newVinParams, 'year'),
              styles: get(newVinParams, 'styles', []),
              styleId: get(newVinParams, 'styleId'),
            },
            normalizedVin
          );

          if (newVinParams) {
            TrackingHandler.fireEvent('pc_can_decode_vin', {
              event_data: { ...TRACKING_PARAMS, value: normalizedVin },
            });
            setErrorMessage(null);
          }

          if (!skipInputChange) {
            onInputChange();
          }
        });
    },
    [apiMetrics, errorMessageText, onInputChange, updateVinParams]
  );

  useEffect(() => {
    if (defaultVin) {
      updateVinData(normalizeVin(defaultVin), true);
    }

    return () => {
      [inventoryPromiseCancelable, stylesPromiseCancelable].forEach(value => get(value, 'current.cancel', noop)());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnBlur = useCallback(
    e => {
      const inputVin = normalizeVin(get(e, 'target.value', '').trim());
      const normalizedDefaultVin = normalizeVin(defaultVin);
      if (inputVin && inputVin !== normalizedDefaultVin) {
        updateVinData(inputVin);
      }
    },
    [defaultVin, updateVinData]
  );

  const handleKeyPress = useCallback(
    e => {
      if (e.key === ENTER_KEY_VALUE) {
        handleOnBlur(e);
      }
    },
    [handleOnBlur]
  );

  const handleInputChange = useCallback(({ target: { value } }) => {
    setVinValue(normalizeVin(value));
  }, []);

  const renderTarget = useCallback(
    (targetProps, ref) => (
      <span {...targetProps} aria-label={null} ref={ref}>
        <span className="text-underline">Where do I get the VIN?</span> <i className="icon-info" aria-hidden />
      </span>
    ),
    []
  );

  return (
    <div className={classnames('vin-input', className)}>
      <div className="d-flex justify-content-between align-items-center mb-0_25">
        <Label className=" small mb-0" for="pc-vin">
          <div className="text-gray-darker">VIN</div>
        </Label>
        <PopoverItem
          className="small text-primary-darker"
          renderTarget={renderTarget}
          screenReaderTooltipLabel="VIN location"
        >
          Enter the VIN of the vehicle the dealer quoted you. The car&apos;s vehicle identification number (VIN) is
          unique, and is composed of 17 characters (digits and capital letters). The VIN is listed on the car’s window
          sticker, or you can ask the dealership to send you the VIN number.
        </PopoverItem>
      </div>

      <Input
        value={vinValue}
        id="pc-vin"
        type="text"
        placeholder="Enter VIN"
        className="px-1 py-0_5 text-gray-darker"
        onChange={handleInputChange}
        onBlur={handleOnBlur}
        onKeyPress={handleKeyPress}
        autoCapitalize="characters"
      />
      {!!errorMessage && <div className="text-danger small mt-0_5">{errorMessage}</div>}
    </div>
  );
}

VinInputUI.propTypes = {
  updateVinParams: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  defaultVin: PropTypes.string,
  errorMessageText: PropTypes.string,
  apiMetrics: ApiMetrics,
};

export const VinInput = withMetrics(VinInputUI, true);
